export interface Scores { installation_score: number; recommendation_score: number; timeliness_score?: number; comment: string }
export interface FeedbackResponse {
  installation_score?: number | null; recommendation_score?: number | null; timeliness_score?: number | null;
  answer?: 'fine' | 'not_fine' | null;
}
export function feedbackSummary(response: FeedbackResponse): string {
  if (response.installation_score != null || response.recommendation_score != null || response.timeliness_score != null) {
    return `Installation: ${response.installation_score ?? '—'}/5 · Recommendation: ${response.recommendation_score ?? '—'}/5${response.timeliness_score != null ? ` · On time / tidy: ${response.timeliness_score}/5` : ''}`;
  }
  return response.answer === 'fine' ? 'Legacy response: Fine' : response.answer === 'not_fine' ? 'Legacy response: Not fine' : 'Response not recorded';
}
export function feedbackCategory(response: FeedbackResponse): string {
  const scores = [response.installation_score, response.recommendation_score, response.timeliness_score].filter((s): s is number => s != null);
  if (scores.length) return scores.some(s => s <= 2) ? 'not_fine' : 'fine';
  return response.answer || '';
}
export interface OldCustomerDetails { phone: string; name: string; city: string; install_year: number; installed_by: string; vertical_slug: string }
export function installationAttribution(kind: string, dealerName: string): string {
  if (kind === 'toyama') return 'Toyama';
  if (kind !== 'dealer' || !dealerName.trim()) throw new Error('Enter the dealer name.');
  return dealerName.trim();
}
export async function copyReviewLink(link: string, write: (text: string) => Promise<void>): Promise<void> {
  const url = new URL(link.trim());
  if (!['https:', 'http:'].includes(url.protocol)) throw new Error('Enter an HTTP or HTTPS review URL.');
  await write(link.trim());
}
export interface QueueClipboardState { pending: boolean; generation: number }
export interface QueueActionDraft {
  outcome: string; confirmed: boolean; closed: boolean; review_link_sent: boolean;
  phone_reviewed: boolean; intake_reviewed: boolean; name: string; city: string; merge_person_id?: number;
}
/** A record can sit in several queue sections; pass all of them so each section's review is kept. */
export function queueActionPayload(section: string | readonly string[], original: { customer_name?: string | null; name?: string | null; city?: string | null }, draft: QueueActionDraft): Record<string, unknown> {
  const has = (key: string) => (typeof section === 'string' ? section === key : section.includes(key));
  const payload: Record<string, unknown> = {
    outcome: draft.outcome.trim(), confirmed: draft.confirmed, closed: draft.closed, review_link_sent: draft.review_link_sent,
    phone_reviewed: has('phone_mismatch') && draft.phone_reviewed,
    intake_reviewed: has('old_customers') && draft.intake_reviewed
  };
  if (has('old_customers')) {
    if (draft.name.trim() !== (original.customer_name || original.name || '').trim()) payload.name = draft.name.trim();
    if (draft.city.trim() !== (original.city || '').trim()) payload.city = draft.city.trim();
    if (draft.merge_person_id !== undefined) payload.merge_person_id = draft.merge_person_id;
  }
  return payload;
}
export function queuePhonePresentation(row: { phone?: string | null; alternate_phone?: string | null; verified_phone?: string | null; verified_at?: string | null }): string[] {
  return [
    `Booked/on-file phone: ${row.phone || 'Not recorded'}`,
    ...(row.alternate_phone ? [`Entered alternate phone: ${row.alternate_phone}`] : []),
    row.verified_at ? (row.verified_phone ? `Phone verified: ${row.verified_phone} · ${row.verified_at}` : `Phone verification recorded · ${row.verified_at}; verified number unavailable`) : 'Unverified — no phone verification recorded'
  ];
}
export function queueActionBlocked(state: QueueClipboardState, saving = false): boolean { return state.pending || saving; }
export function changeQueueModal(state: QueueClipboardState, saving = false): boolean {
  if (queueActionBlocked(state, saving)) return false;
  state.generation++;
  return true;
}
export async function runQueueClipboard(state: QueueClipboardState, isCurrent: () => boolean, work: () => Promise<void>, success: () => void, failure: () => void): Promise<void> {
  if (state.pending) return;
  const generation = state.generation;
  state.pending = true;
  try {
    await work();
    if (state.generation === generation && isCurrent()) success();
  } catch {
    if (state.generation === generation && isCurrent()) failure();
  } finally { state.pending = false; }
}
// Explicit whitelist: an invitation must not contain scores or a proof.
export function oldCustomerInvitation(details: OldCustomerDetails): OldCustomerDetails {
  return { phone: details.phone.trim(), name: details.name.trim(), city: details.city.trim(), install_year: details.install_year, installed_by: details.installed_by, vertical_slug: details.vertical_slug };
}
export function needsExplanation(scores: Scores): boolean {
  return [scores.installation_score, scores.recommendation_score, scores.timeliness_score].some(s => s !== undefined && s > 0 && s <= 2);
}
export function scoreError(scores: Scores): string {
  if (![scores.installation_score, scores.recommendation_score].every(s => Number.isInteger(s) && s >= 1 && s <= 5)) return 'Please answer both required questions.';
  if (scores.timeliness_score !== undefined && (!Number.isInteger(scores.timeliness_score) || scores.timeliness_score < 1 || scores.timeliness_score > 5)) return 'Choose a score between 1 and 5.';
  return needsExplanation(scores) && !scores.comment.trim() ? 'Please tell us what went wrong.' : '';
}
export function happyCodeLink(origin: string): string { return new URL('/#/happy-code', origin).href; }
export function canUseHappyQueue(user?: { role: string; issues_only?: boolean; dispatch_only?: boolean; intake_coordinator?: boolean }, supportAccess = false, crmVerticals: readonly { slug: string }[] = []): boolean {
  // Display names change, but only these three stable sales-pipeline slugs
  // qualify a coordinator's full-CRM grants for the callback queue.
  const coordinatorAccess = user?.intake_coordinator === true && crmVerticals.some(vertical => ['homeowners', 'lighting', 'dealers'].includes(vertical.slug));
  return !!user && !user.issues_only && !user.dispatch_only && (user.role === 'admin' || supportAccess || coordinatorAccess);
}

// ---- Call queue presentation ---------------------------------------------

export type QueueReason = 'negatives' | 'due_calls' | 'feedback_pending' | 'phone_mismatch' | 'old_customers';
/** Priority order: unhappy customers first, then happy calls, missing feedback and data checks. */
export const queueReasonOrder: readonly QueueReason[] = ['negatives', 'due_calls', 'feedback_pending', 'phone_mismatch', 'old_customers'];
export const queueReasons: Record<QueueReason, { label: string; plural: string; tone: 'danger' | 'ok' | 'info' | 'warn'; hint: string }> = {
  negatives: { label: 'Unhappy customer', plural: 'Unhappy customers', tone: 'danger', hint: 'Scored 2 or below. Call today, get it fixed, then confirm.' },
  due_calls: { label: 'Happy call due', plural: 'Happy calls due', tone: 'ok', hint: 'Feedback is in. Call to check everything is fine and close the job.' },
  feedback_pending: { label: 'Feedback not in', plural: 'Waiting for feedback', tone: 'info', hint: 'Installed or serviced, but no feedback yet. Call and share the feedback link.' },
  phone_mismatch: { label: 'New number to check', plural: 'Numbers to check', tone: 'warn', hint: 'Feedback came from a different number. Decide which number is the customer\'s.' },
  old_customers: { label: 'Old customer to confirm', plural: 'Old customers to confirm', tone: 'warn', hint: 'Service request from an existing customer. Confirm who they are before merging.' }
};
export interface QueueEntry<T extends { id: number }> { row: T; reasons: QueueReason[] }
/** One entry per customer record, carrying every reason it is in the queue, in priority order. */
export function groupQueue<T extends { id: number }>(queue: Partial<Record<QueueReason, T[]>> | undefined): QueueEntry<T>[] {
  const byId = new Map<number, QueueEntry<T>>();
  for (const reason of queueReasonOrder) {
    for (const row of queue?.[reason] ?? []) {
      const entry = byId.get(row.id);
      if (entry) { if (!entry.reasons.includes(reason)) entry.reasons.push(reason); }
      else byId.set(row.id, { row, reasons: [reason] });
    }
  }
  return [...byId.values()];
}
/** Scores at or below 2 are what put a customer in the unhappy list. */
export function queueScores(row: { installation_score?: number | null; recommendation_score?: number | null; timeliness_score?: number | null }): Array<{ label: string; score: number; low: boolean }> {
  const pairs: Array<[string, number | null | undefined]> = [['Installation', row.installation_score], ['Would recommend', row.recommendation_score], ['On time & tidy', row.timeliness_score]];
  return pairs.flatMap(([label, score]) => (typeof score === 'number' ? [{ label, score, low: score <= 2 }] : []));
}
